import { useCallback, useMemo, useState, type ReactNode } from 'react';
import type { ScopeTransition } from '../types';
import { UnsavedChangesPopup } from '@/components/shared/unsaved-changes-popup';
import { useDirtyState, useScopeTransition } from '@/hooks/useScopeChangeHooks';
import { ScopeResolutionContext } from '../contexts';

interface ResolutionProviderProps {
  children: ReactNode;
}

export const ResolutionProvider = ({ children }: ResolutionProviderProps) => {
  const [blockedTransition, setBlockedTransition] =
    useState<ScopeTransition | null>(null);
  const { clearDirtyState } = useDirtyState();
  const { clearTransition } = useScopeTransition();

  const discardChanges = useCallback(() => {
    if (!blockedTransition) return;
    clearDirtyState(blockedTransition?.from);
    blockedTransition.run();
    clearTransition();
    setBlockedTransition(null);
  }, [blockedTransition, clearTransition, clearDirtyState]);

  const cancelChanges = useCallback(() => {
    setBlockedTransition(null);
    clearTransition();
  }, [clearTransition]);

  const requireResolution = useCallback(
    (transition: ScopeTransition) => {
      setBlockedTransition(transition);
    },
    [setBlockedTransition]
  );

  const value = useMemo(() => {
    return { requireResolution };
  }, [requireResolution]);

  return (
    <ScopeResolutionContext.Provider value={value}>
      {children}
      <UnsavedChangesPopup
        title={blockedTransition?.title}
        body={blockedTransition?.body}
        cancelLabel={blockedTransition?.cancelLabel}
        discardLabel={blockedTransition?.discardLabel}
        onCancel={cancelChanges}
        onDiscard={discardChanges}
        open={!!blockedTransition}
      />
    </ScopeResolutionContext.Provider>
  );
};
